/**
 * Classificacao das falhas de rede do ApiService para o Sentry (MAN-116).
 *
 * Sem fingerprint explicito o Sentry agrupa `TypeError: Failed to fetch` pela
 * pilha, e a pilha muda a cada chamador (`index N (async Promise.all)`, hash do
 * chunk a cada build). Resultado: uma queda de 13 minutos da API abriu ~20
 * issues novas. Aqui a falha vira um grupo estavel por tipo + rota da API.
 */

export type NetworkFailureKind = 'timeout' | 'network_error' | 'aborted';

/** Timeout do proprio cliente (fetchWithTimeout), distinto de abort externo. */
export class RequestTimeoutError extends Error {
    constructor(timeoutMs: number, url: string) {
        super(`Timeout apos ${timeoutMs}ms em ${url}`);
    }
}

export function classifyNetworkFailure(cause: unknown): NetworkFailureKind {
    if (cause instanceof RequestTimeoutError) return 'timeout';
    // Abort que nao veio do nosso timeout: navegacao, fechamento de aba ou
    // cancelamento pelo proprio usuario.
    if (cause instanceof Error && cause.name === 'AbortError') return 'aborted';
    return 'network_error';
}

export interface NetworkFailureContext {
    /** `navigator.onLine` no momento do reporte. */
    online: boolean;
    /** A pagina esta sendo descarregada (pagehide): o browser cancela os fetch em voo. */
    unloading: boolean;
}

/**
 * Falha do lado do usuario nao e bug nem queda da API: browser offline, aba
 * fechando ou requisicao abortada. So o resto vira evento.
 */
export function shouldReportNetworkFailure(kind: NetworkFailureKind, ctx: NetworkFailureContext): boolean {
    if (kind === 'aborted') return false;
    if (!ctx.online) return false;
    if (ctx.unloading) return false;
    return true;
}

export function networkFailureFingerprint(kind: NetworkFailureKind, endpointPattern: string): string[] {
    return ['api-network-failure', kind, endpointPattern];
}

/** `navigator.onLine === false` e confiavel; `true` so diz que ha alguma interface. */
export function isBrowserOnline(): boolean {
    return typeof navigator === 'undefined' || navigator.onLine !== false;
}
